/**
 * Session segmentation config: fetch which skin concerns are allowed (key === "yes")
 * so we can generate masks only for those and skip the rest.
 */
import { SESSION_APP_DATA_BASE } from '../config/sessionAppData';
import { fetchWithTimeout } from './fetchWithTimeout';

export const SEGMENTATION_API_BASE = SESSION_APP_DATA_BASE;

export function parseFeatureToggle(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    return v === 'yes' || v === 'true' || v === '1' || v === 'on';
  }
  return false;
}

export function toYesNoValue(value) {
  if (value == null) return null;
  return parseFeatureToggle(value) ? 'yes' : 'no';
}

/**
 * Parse segmentation API payload (same rules as SkinRoutinePage).
 * URL: `${SEGMENTATION_API_BASE}/${token}/segmentation`
 */
export function parseSegmentationPayload(data) {
  const fields = Array.isArray(data?.fields) ? data.fields : [];
  const segmentationMap = data?.segmentation && typeof data.segmentation === 'object'
    ? data.segmentation
    : null;

  let concernsEnabled = segmentationMap?.['Skin Concerns'] != null
    ? parseFeatureToggle(segmentationMap['Skin Concerns'])
    : true;
  let conditionsEnabled = segmentationMap?.['Skin Conditions'] != null
    ? parseFeatureToggle(segmentationMap['Skin Conditions'])
    : true;
  let advancedCharts = segmentationMap?.['Advanced graphical charts'] != null
    ? parseFeatureToggle(segmentationMap['Advanced graphical charts'])
    : true;

  const allowed = new Set();
  fields.forEach((field) => {
    const label = String(field?.label || '').trim();
    if (label === 'Skin Concerns' && segmentationMap?.['Skin Concerns'] == null) {
      concernsEnabled = parseFeatureToggle(field?.key);
    }
    if (label === 'Skin Conditions' && segmentationMap?.['Skin Conditions'] == null) {
      conditionsEnabled = parseFeatureToggle(field?.key);
    }
    if (label === 'Advanced graphical charts' && segmentationMap?.['Advanced graphical charts'] == null) {
      advancedCharts = parseFeatureToggle(field?.key);
    }
    if (field?.key !== 'yes' || !Array.isArray(field?.options)) return;
    field.options.forEach((opt) => {
      const name = normalizeSegmentationOption(opt);
      if (name) allowed.add(name);
    });
  });

  const hasProductFlag = Object.prototype.hasOwnProperty.call(data || {}, 'product_suggestion')
    || Object.prototype.hasOwnProperty.call(data || {}, 'product');
  const productSuggestion = hasProductFlag
    ? parseFeatureToggle(data?.product_suggestion ?? data?.product)
    : true;
  const productActions = resolveSegmentationProductActions(data);

  const productRecommendationSource =
    data?.product_recommendation_source === 'products_page' ? 'products_page' : 'database';

  const limit = data?.product_suggest_limit;
  const num = typeof limit === 'number' ? limit : parseInt(limit, 10);
  const productSuggestLimit = productRecommendationSource === 'products_page'
    ? null
    : (Number.isNaN(num) || num < 1 ? null : num);

  const mode = String(data?.ai_mode || '').trim().toLowerCase();
  const aiMode = mode === 'advanced' || mode === 'basic' ? mode : null;
  const productsPageUrl = String(data?.products_page_url || '').trim();
  const productsPageUrls = Array.isArray(data?.products_page_urls)
    ? data.products_page_urls.map((u) => String(u || '').trim()).filter(Boolean)
    : (productsPageUrl ? [productsPageUrl] : []);

  return {
    showConcerns: concernsEnabled,
    showConditions: conditionsEnabled,
    advancedCharts,
    productSuggestion,
    addToCart: productActions.addToCart,
    productLink: productActions.productLink,
    allowedNames: allowed.size > 0 ? allowed : (fields.length > 0 ? new Set() : null),
    aiGeneratedSummary: toYesNoValue(data?.ai_generated_summary),
    aiMode,
    productSuggestLimit,
    productRecommendationSource,
    productsPageUrl: productsPageUrls[0] || productsPageUrl,
    productsPageUrls,
  };
}

/** Product action flags from segmentation API (`add_to_cart`, `product_link`). */
export function resolveSegmentationProductActions(data) {
  return {
    addToCart: Object.prototype.hasOwnProperty.call(data || {}, 'add_to_cart')
      ? parseFeatureToggle(data.add_to_cart)
      : true,
    productLink: Object.prototype.hasOwnProperty.call(data || {}, 'product_link')
      ? parseFeatureToggle(data.product_link)
      : true,
  };
}

export function isValidProductUrl(url) {
  if (url == null || typeof url !== 'string') return false;
  const trimmed = url.trim();
  return trimmed.length > 0;
}

/**
 * Map API option text to our class_name / displayName (must match backend and SkinResultsPage).
 */
export function normalizeSegmentationOption(option) {
  if (!option || typeof option !== 'string') return null;
  const s = option.trim();
  const normalized = s
    .toLowerCase()
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (normalized.startsWith('uneven') || normalized.includes('uneven skin') || normalized.includes('uneven tone')) return 'UnevenSkin';
  if (normalized.startsWith('hydration')) return 'Hydration';
  if (normalized.includes('smoothness')) return 'Smoothness';
  if (normalized.startsWith('oxygen')) return 'Oxygen';
  if (normalized.startsWith('dullness')) return 'Dullness';

  const labelMap = {
    'wrinkles': 'Wrinkles',
    'dry skin': 'Dry skin',
    'oily skin': 'Oily skin',
    'dark circle': 'Dark circle',
    'acne': 'Acne',
    'red spot': 'Red spot',
    'pigmentation': 'Pigmentation',
    'mole': 'Mole',
    'dark spots': 'Dark spots',
    'whiteheads': 'Whiteheads',
    "crow's feet": "Crow's feet",
    'open pores': 'Open Pores',
    'blackheads': 'Blackheads',
    'clogged pores': 'Clogged Pores',
    'eye bags': 'Eye Bags',
    'eye bag': 'Eye Bags',
    'unknown': 'Unknown',
  };

  return labelMap[normalized] || s;
}

/**
 * Fetch segmentation config for session and return set of allowed condition names (key === "yes" only).
 * When key is "no" (e.g. "Skin Concerns" with options []), nothing is added to allowed, so we return
 * an empty set and the backend will skip all those concerns.
 * Returns null only if no token, request fails, or response has no fields array.
 * Returns empty Set when segmentation exists but no field has key "yes" (so backend must skip all).
 */
export async function fetchAllowedConditionNames(token) {
  if (!token) return null;
  try {
    const res = await fetchWithTimeout(
      `${SEGMENTATION_API_BASE}/${encodeURIComponent(token)}/segmentation`,
      { timeoutMs: 20000 }
    );
    if (!res.ok) return null;
    const data = await res.json().catch(() => ({}));
    const fields = data?.fields ?? data?.data?.fields;
    if (!Array.isArray(fields)) return null;
    const allowed = new Set();
    fields.forEach((field) => {
      if (field.key !== 'yes') return;
      const opts = field.options;
      if (!Array.isArray(opts)) return;
      opts.forEach((opt) => {
        const name = normalizeSegmentationOption(opt);
        if (name) allowed.add(name);
      });
    });
    return allowed;
  } catch {
    return null;
  }
}
